import { useMemo, forwardRef, useImperativeHandle } from 'react';
import { useTable, useSortBy, useFilters } from 'react-table';
import { FaArrowDown, FaArrowUp, FaSort } from 'react-icons/fa';

import { formatDateCellCustom, formatNumberWithCommas } from '../../helpers/reactTable';
import { MultiSelectColumnFilter, multiSelectFilter } from './tableFilters';



const OrdersTable = forwardRef(({ orders }, ref) => {
  const data = useMemo(() => orders, [orders]);
  
  const getOrderName = (order) => {
    switch (order.orderType) {
      case 'audience':
        return order.audienceName || 'Audience Order';
      case 'enhancement':
        return order.packageName || 'Enhancement Package';
      case 'insight':
        return order.seedFileName || 'Profile Report';
      default:
        return 'Unknown Order';
    }
  };

  const getOrderType = (orderType) => {
    switch (orderType) {
      case 'audience':
        return 'Audience Order';
      case 'enhancement':
        return 'Enhancement';
      case 'audience-enhancement':
        return 'Enhancement';
      case 'audience-profile':
        return 'Profile Report';
      case 'insight':
        return 'Profile Report';
      default:
        return orderType;
    }
  };

  const getPaymentType = (order) => {
    // First check the user's payment type preference
    if (order.user?.paymentType) {
      return order.user.paymentType;
    }

    // Fallback to order-level detection for older orders
    if (order.invoiceId) {
      return 'Invoice';
    }
    if (order.paymentMethod?.brand) {
      return 'Stripe';
    }
    return 'N/A';
  };

  const getAttributes = (order) => {
    const attributes = [];

    if (order.orderType === 'audience' && order.recordsIncluded) {
      attributes.push('Download CSV to access');
    }

    if (order.orderType === 'insight') {
      attributes.push('Download CSV to access');
    }

    return attributes.length > 0 ? attributes.join(', ') : 'N/A';
  };

  // Helper functions to format data for picklist filters
  const getFormattedDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateCellCustom({
      value: dateString,
      pattern: 'MMM d, yyyy',
    });
  };

  const getFormattedPrice = (amount) => {
    if (!amount) return 'N/A';
    return `$${amount}`;
  };

  const getFormattedRecordVolume = (recordsIncluded) => {
    if (!recordsIncluded) return 'N/A';
    return formatNumberWithCommas({ value: recordsIncluded });
  };

  const getTruncatedOrderId = (id) => {
    if (!id) return 'N/A';
    return id.substring(0, 8) + '...';
  };

  const columns = useMemo(
    () => [
      {
        Header: 'User',
        accessor: 'user.email',
        Cell: ({ value }) => value || 'N/A',
        Filter: MultiSelectColumnFilter,
        filter: multiSelectFilter,
      },
      {
        Header: 'Payment Type',
        id: 'paymentType',
        accessor: (row) => getPaymentType(row),
        Cell: ({ value }) => value,
        Filter: MultiSelectColumnFilter,
        filter: multiSelectFilter,
      },
      {
        Header: () => (
          <div className="flex items-center">
            <span>Order date</span>
            <FaSort className="ml-2 text-gray-400" size={12} />
          </div>
        ),
        id: 'orderDate',
        accessor: (row) => getFormattedDate(row.createdDate),
        Cell: ({ value }) => value,
        disableFilters: true,
      },
      {
        Header: () => (
          <div className="flex items-center">
            <span>Order Id</span>
            <FaSort className="ml-2 text-gray-400" size={12} />
          </div>
        ),
        id: 'orderId',
        accessor: (row) => getTruncatedOrderId(row.id),
        Cell: ({ value, row }) => (
          <span title={row.original.id}>
            {value}
          </span>
        ),
        disableFilters: true,
      },
      {
        Header: () => (
          <div className="flex items-center">
            <span>Order Name</span>
            <FaSort className="ml-2 text-gray-400" size={12} />
          </div>
        ),
        id: 'orderName',
        accessor: (row) => getOrderName(row),
        Cell: ({ value }) => value,
        disableFilters: true,
      },
      {
        Header: 'Order type',
        id: 'orderType',
        accessor: (row) => getOrderType(row.orderType),
        Cell: ({ value }) => value,
        Filter: MultiSelectColumnFilter,
        filter: multiSelectFilter,
      },
      {
        Header: () => (
          <div className="flex items-center">
            <span>Price</span>
            <FaSort className="ml-2 text-gray-400" size={12} />
          </div>
        ),
        id: 'price',
        accessor: (row) => getFormattedPrice(row.amount),
        Cell: ({ value }) => value,
        disableFilters: true,
      },
      {
        Header: () => (
          <div className="flex items-center">
            <span>Record Volume</span>
            <FaSort className="ml-2 text-gray-400" size={12} />
          </div>
        ),
        id: 'recordVolume',
        accessor: (row) => getFormattedRecordVolume(row.recordsIncluded),
        Cell: ({ value }) => value,
        disableFilters: true,
      },
      {
        Header: 'Attributes',
        id: 'attributes',
        accessor: (row) => getAttributes(row),
        Cell: ({ value }) => value,
        disableFilters: true,
      },
    ],
    []
  );

  const tableInstance = useTable(
    {
      columns,
      data,
    },
    useFilters,
    useSortBy
  );
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow, setAllFilters } =
    tableInstance;

  useImperativeHandle(ref, () => ({
    setAllFilters,
  }));

  return (
    <div className="h-full w-full overflow-auto pb-14">
      <div className="mb-2 text-sm text-gray-600">
        Showing {rows.length} of {data.length} orders
      </div>
      <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="border p-2 text-left hover:bg-gray-100 bg-gray-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      {column.render('Header')}
                      {column.isSorted && (
                        <span className="pl-2">
                          {column.isSortedDesc ? <FaArrowDown /> : <FaArrowUp />}
                        </span>
                      )}
                    </div>
                    <div className="flex items-center">
                      {column.canFilter ? column.render('Filter') : null}
                    </div>
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td {...cell.getCellProps()} className="border p-2">
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

OrdersTable.displayName = 'OrdersTable';

export default OrdersTable;
